/**
 * Base64 VLQ, the encoding source maps use: five data bits per character, a
 * sixth bit marking "more to come", and the sign in the low bit of the first
 * group.
 *
 * This replaces the `vlq` npm package, which wrote the strings we have already
 * persisted, and keeps its wire format: the output is identical for every value
 * that package encodes correctly. That package works through JavaScript's
 * bitwise operators, which coerce to signed 32 bits, so it silently returns a
 * different number for anything outside +/-2^31. Seconds since the epoch cross
 * that line in January 2038.
 *
 * Values that fit in 32 bits take the same bitwise path `vlq` did. Wider ones go
 * through `encodeWide` and `decodeWide`, which do the arithmetic with `* 32` and
 * `Math.floor(x / 32)` and so reach the whole safe-integer span at no cost in
 * output size: the format is variable-length, so a larger value simply uses more
 * characters.
 *
 * The split also matters for speed. V8 records, per operation, which kinds of
 * number it has seen, and once a value beyond +/-2^30 passes through an
 * arithmetic operation, that operation compiles to float math for every later
 * call. Running every value through the wide arithmetic instead benchmarked up
 * to 3x slower than `vlq` once it had seen a single timestamp.
 */
export namespace VLQ64 {
  const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

  /** charCode -> value, so decode never allocates a single-character string. */
  const VALUES = new Int8Array(128).fill(-1);
  for (let i = 0; i < CHARS.length; ++i)
    VALUES[CHARS.charCodeAt(i)] = i;

  const DIGIT_BITS = 5;
  const CONTINUATION = 32;
  const MASK = 31;

  /**
   * Six characters carry 30 bits, which V8 keeps as a small integer. A value
   * that needs a seventh is finished by `decodeWide`.
   */
  const MAX_NARROW_SHIFT = 30;

  /**
   * Largest magnitude this encoding can carry. The sign rides in the low bit, so
   * a value becomes `v * 2` or `-v * 2 + 1` before being split into digits, and
   * that has to stay an exact integer - which caps us one bit below
   * `Number.MAX_SAFE_INTEGER`, minus one for the negative case's extra bit.
   *
   * As seconds since the epoch this is past the year 140,000,000.
   */
  export const MAX_MAGNITUDE = 2 ** 52 - 1;

  /** @throws If a value is not an integer this encoding can carry exactly. */
  export function encode(values: number[]): string {
    let result = '';
    for (const value of values) {
      // Any int32 but -2^31 doubles into 32 unsigned bits, which `>>>` reads back
      // correctly even when `<<` wrapped them negative.
      if ((value | 0) !== value || value === -0x80000000) {
        result += encodeWide(value);
        continue;
      }
      let rest = value < 0 ? (-value << 1) | 1 : value << 1;
      do {
        let digit = rest & MASK;
        rest >>>= DIGIT_BITS;
        if (rest > 0)
          digit |= CONTINUATION;
        result += CHARS[digit];
      } while (rest > 0);
    }
    return result;
  }

  function encodeWide(value: number): string {
    if (!Number.isInteger(value) || value < -MAX_MAGNITUDE || value > MAX_MAGNITUDE)
      throw new Error(`Cannot VLQ-encode ${value}: expected an integer within +/-${MAX_MAGNITUDE}`);
    let result = '';
    // The sign rides in the low bit, so the magnitude doubles.
    let rest = value < 0 ? -value * 2 + 1 : value * 2;
    do {
      let digit = rest % CONTINUATION;
      rest = Math.floor(rest / CONTINUATION);
      if (rest > 0)
        digit += CONTINUATION;
      result += CHARS[digit];
    } while (rest > 0);
    return result;
  }

  /** @throws If the string holds a character outside the alphabet. */
  export function decode(encoded: string): number[] {
    const result: number[] = [];
    let value = 0;
    let shift = 0;
    for (let i = 0; i < encoded.length; ++i) {
      const digit = digitAt(encoded, i);
      value += (digit & MASK) << shift;
      if (digit & CONTINUATION) {
        shift += DIGIT_BITS;
        if (shift === MAX_NARROW_SHIFT) {
          i = decodeWide(encoded, i + 1, value, result);
          value = 0;
          shift = 0;
        }
        continue;
      }
      // The `vlq` package writes -2147483648 as a negative zero, because its
      // `-num << 1` overflows to 0, and reads it back through the same special
      // case. Honour that so strings it wrote still decode. Our own encoder emits
      // the canonical multi-character form for that value.
      if (value === 1)
        result.push(-0x80000000);
      else
        result.push(value & 1 ? -(value >>> 1) : value >>> 1);
      value = 0;
      shift = 0;
    }
    return result;
  }

  /**
   * Finishes a value that did not fit in six characters, given the 30 bits read
   * so far, and pushes it onto `result`.
   *
   * @returns The index of the value's last character.
   */
  function decodeWide(encoded: string, start: number, value: number, result: number[]): number {
    let scale = 2 ** MAX_NARROW_SHIFT;
    for (let i = start; i < encoded.length; ++i) {
      const digit = digitAt(encoded, i);
      value += (digit & MASK) * scale;
      if (digit & CONTINUATION) {
        scale *= CONTINUATION;
        continue;
      }
      const negative = value % 2 === 1;
      const magnitude = (value - (negative ? 1 : 0)) / 2;
      if (negative && magnitude === 0)
        result.push(-0x80000000);
      else
        result.push(negative ? -magnitude : magnitude);
      return i;
    }
    return encoded.length;
  }

  function digitAt(encoded: string, i: number): number {
    const code = encoded.charCodeAt(i);
    const digit = code < 128 ? VALUES[code] : -1;
    if (digit < 0)
      throw new Error(`Cannot VLQ-decode: invalid character ${JSON.stringify(encoded[i])} at ${i}`);
    return digit;
  }
}
